import React, {forwardRef, useEffect, useRef} from 'react'
import {Box, Checkbox as ChakraCheckbox, CheckboxProps, FormControl, FormErrorMessage, VisuallyHidden} from '@chakra-ui/react'

interface CustomCheckboxProps extends CheckboxProps {
    errorMessage?: string
}

/**
 * @name Checkbox
 *
 * @component
 *
 * @description
 * A custom Checkbox component that wraps Chakra UI's Checkbox with additional functionality.
 *
 * @param {CustomCheckboxProps} props - The properties passed to the Checkbox component.
 * @param {string} props.errorMessage - An optional error message to display when the checkbox is in an error state.
 * @param {string} props.name - The name of the checkbox, used for form control identification.
 * @param {boolean} props.isDisabled - A flag indicating whether the checkbox is disabled.
 * @param {React.ReactNode} props.children - The content to be displayed within the checkbox.
 * @param {React.Ref<HTMLInputElement>} ref - A ref to the underlying input element.
 *
 * @returns {JSX.Element} The rendered Checkbox component.
 */
const Checkbox = forwardRef<HTMLInputElement, CustomCheckboxProps>(({errorMessage, id, isDisabled, children, ...rest}, ref) => {
    const checkboxContainerRef = useRef<HTMLDivElement | null>(null)
    const value = rest.value ? rest.value.toString() : ''

    /**
     * Avoid redundance of `aria-checked` attribute.
     */
    useEffect(() => {
        if (checkboxContainerRef.current) {
            const inputElement = checkboxContainerRef.current.querySelector('input[type="checkbox"]')

            if (inputElement) {
                inputElement.setAttribute('checked', String(rest.isChecked))

                if (inputElement.getAttribute('aria-checked') !== null) {
                    inputElement.removeAttribute('aria-checked')
                }
            }
        }
    }, [rest.isChecked])

    return (
        <FormControl id={id} isInvalid={!!errorMessage} isDisabled={isDisabled} aria-labelledby={value}>
            <Box ref={checkboxContainerRef}>
                <VisuallyHidden id={value}>{value}</VisuallyHidden>
                <ChakraCheckbox {...rest} ref={ref} data-testid={'checkbox'} aria-checked={rest.isChecked ? 'true' : 'false'}>
                    {children}
                </ChakraCheckbox>
            </Box>
            {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
        </FormControl>
    )
})

Checkbox.displayName = 'Checkbox'

export {Checkbox}
